import { c as _c } from "react/compiler-runtime";
import { ChainId, DEFAULT_ADMIN_ROLE, type NftPoolConfig, type PoolId, type RequiredNftPoolChainType, chainSettings, fallbackChainId, fallbackMulticall, multicallAddress } from "@/constants/index";
import { getPoolSettings } from "@/util/index";
import { useEffect, useState } from "react";
import { type Address, formatUnits } from "viem";
import { useConnection, useReadContracts } from "wagmi";
import { nftAbi, stakingAbi, tokenAbi } from "@/constants/abi";
import { twNftStaking } from '../constants/abi/twNftStaking';
import { useNftBalance } from "./useNftBalance";
import { useStakedNftBalance } from "./useStakedNftBalance";

/**
 * Custom hook for fetching data related to NFT staking pool.
 * @param poolId - The ID of the staking pool.
 * @param account - Optional account address.
 * @returns An object containing staking data, error status, loading status, refetch function, and query key.
 */
export function useNftStakingPool(poolId, account) {
  const $ = _c(251);
  const {
    chainId
  } = useConnection();
  const t0 = chainId as RequiredNftPoolChainType ?? fallbackChainId as RequiredNftPoolChainType;
  let t1;
  if ($[0] !== poolId || $[1] !== t0) {
    t1 = getPoolSettings(t0, poolId);
    $[0] = poolId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currPool = t1;
  const [myTokenIds, setMyTokenIds] = useState(null);
  const [, setMyStakedTokenIds] = useState(null);
  useState(null);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [stakingRewardBalance, setStakingRewardBalance] = useState(0);
  const [staking404RewardBalance, setStaking404RewardBalance] = useState(0);
  const [dn404RewardBalance, setDn404RewardBalance] = useState(0);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = BigInt(0);
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [dn404RewardBalanceBn, setDn404RewardBalanceBn] = useState(t2);
  const [dn404TokenBalance, setDn404TokenBalance] = useState(0);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = BigInt(0);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [rewardBalanceBn, setRewardBalanceBn] = useState(t3);
  const [nftBalance, setNftBalance] = useState(0);
  const [isOwner, setIsOwner] = useState(false);
  const [nftCost, setNftCost] = useState(0);
  const [totalSupply, setTotalSupply] = useState(0);
  const [totalStakedNft, setTotalStakedNft] = useState(0);
  const [maxSupply, setMaxSupply] = useState(0);
  const [rewardsAmount, setRewardsAmount] = useState(0);
  const [totalStaked, setTotalStaked] = useState(0);
  const [dn404TotalSupply, setDn404TotalSupply] = useState(0);
  useState(0);
  const [isApproved, setIsApproved] = useState(false);
  const [wrapAllowance, setWrapAllowance] = useState(0);
  const [twTokenIds, setTwTokenIds] = useState(null);
  const [twPendingRewards, setTwPendingRewards] = useState(0);
  const {
    myTokenIds: tokenIds,
    refetch: refreshIds
  } = useNftBalance(poolId, nftBalance, account);
  const {
    stakedTokenIds,
    pendingRewards,
    refreshData
  } = useStakedNftBalance(poolId, totalStaked, account);
  const [twAllowance, setTwAllowance] = useState(0);
  const isThirdweb = currPool.contractType && currPool.contractType === "thirdweb" ? true : false;
  const t4 = isThirdweb ? twNftStaking : stakingAbi;
  let t5;
  if ($[5] !== currPool.address || $[6] !== t4) {
    t5 = {
      address: currPool.address,
      abi: t4
    };
    $[5] = currPool.address;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const stakingContract = t5 as const;
  let t6;
  if ($[8] !== currPool.nftAddress) {
    t6 = {
      address: currPool.nftAddress,
      abi: nftAbi
    };
    $[8] = currPool.nftAddress;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const nftContract = t6 as const;
  let t7;
  if ($[10] !== currPool.rewardAddress) {
    t7 = {
      address: currPool.rewardAddress,
      abi: tokenAbi
    };
    $[10] = currPool.rewardAddress;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const tokenContract = t7 as const;
  let t8;
  if ($[12] !== currPool._404Token) {
    t8 = {
      address: currPool._404Token,
      abi: tokenAbi
    };
    $[12] = currPool._404Token;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const dn404Contract = t8;
  let t9;
  if ($[14] !== currPool._404RewardAddress) {
    t9 = {
      address: currPool._404RewardAddress,
      abi: tokenAbi
    };
    $[14] = currPool._404RewardAddress;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const dn404RewardContract = t9;
  const t10 = account as any;
  let t11;
  if ($[16] !== stakingContract.address || $[17] !== t10) {
    t11 = [t10, stakingContract.address];
    $[16] = stakingContract.address;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== nftContract || $[20] !== t11) {
    t12 = {
      ...nftContract,
      functionName: "isApprovedForAll",
      args: t11
    };
    $[19] = nftContract;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = account as any;
  let t14;
  if ($[22] !== t13) {
    t14 = [t13];
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== nftContract || $[25] !== t14) {
    t15 = {
      ...nftContract,
      functionName: "balanceOf",
      args: t14
    };
    $[24] = nftContract;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const t16 = account as any;
  let t17;
  if ($[27] !== t16) {
    t17 = [t16];
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t17 || $[30] !== tokenContract) {
    t18 = {
      ...tokenContract,
      functionName: "balanceOf",
      args: t17
    };
    $[29] = t17;
    $[30] = tokenContract;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const t19 = stakingContract.address as any;
  let t20;
  if ($[32] !== t19) {
    t20 = [t19];
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== t20 || $[35] !== tokenContract) {
    t21 = {
      ...tokenContract,
      functionName: "balanceOf",
      args: t20
    };
    $[34] = t20;
    $[35] = tokenContract;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t12 || $[38] !== t15 || $[39] !== t18 || $[40] !== t21) {
    t22 = [t12, t15, t18, t21];
    $[37] = t12;
    $[38] = t15;
    $[39] = t18;
    $[40] = t21;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const t23 = multicallAddress[chainId as ChainId ?? ChainId.SHIBARIUM];
  let t24;
  if ($[42] !== t22 || $[43] !== t23) {
    t24 = {
      allowFailure: false,
      contracts: t22,
      multicallAddress: t23
    };
    $[42] = t22;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  const {
    data: tokenData,
    refetch
  } = useReadContracts(t24);
  const t25 = account as any;
  let t26;
  if ($[45] !== t25) {
    t26 = [t25];
    $[45] = t25;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== stakingContract || $[48] !== t26) {
    t27 = {
      ...stakingContract,
      functionName: "countofMyStakedTokens",
      args: t26
    };
    $[47] = stakingContract;
    $[48] = t26;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  let t29;
  if ($[50] !== stakingContract) {
    t28 = {
      ...stakingContract,
      functionName: "owner"
    };
    t29 = {
      ...stakingContract,
      functionName: "rewardsAmount"
    };
    $[50] = stakingContract;
    $[51] = t28;
    $[52] = t29;
  } else {
    t28 = $[51];
    t29 = $[52];
  }
  let t30;
  if ($[53] !== t27 || $[54] !== t28 || $[55] !== t29) {
    t30 = [t27, t28, t29];
    $[53] = t27;
    $[54] = t28;
    $[55] = t29;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  const t31 = multicallAddress[chainId as ChainId ?? ChainId.SHIBARIUM];
  const t32 = !isThirdweb;
  let t33;
  if ($[57] !== t32) {
    t33 = {
      enabled: t32
    };
    $[57] = t32;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== t30 || $[60] !== t31 || $[61] !== t33) {
    t34 = {
      allowFailure: false,
      contracts: t30,
      multicallAddress: t31,
      query: t33
    };
    $[59] = t30;
    $[60] = t31;
    $[61] = t33;
    $[62] = t34;
  } else {
    t34 = $[62];
  }
  const {
    data: stakeData
  } = useReadContracts(t34);
  const t35 = account as any;
  let t36;
  if ($[63] !== t35) {
    t36 = [t35];
    $[63] = t35;
    $[64] = t36;
  } else {
    t36 = $[64];
  }
  let t37;
  if ($[65] !== stakingContract || $[66] !== t36) {
    t37 = {
      ...stakingContract,
      functionName: "getStakeInfo",
      args: t36
    };
    $[65] = stakingContract;
    $[66] = t36;
    $[67] = t37;
  } else {
    t37 = $[67];
  }
  const t38 = account as any;
  let t39;
  if ($[68] !== t38) {
    t39 = [DEFAULT_ADMIN_ROLE, t38];
    $[68] = t38;
    $[69] = t39;
  } else {
    t39 = $[69];
  }
  let t40;
  if ($[70] !== stakingContract || $[71] !== t39) {
    t40 = {
      ...stakingContract,
      functionName: "hasRole",
      args: t39
    };
    $[70] = stakingContract;
    $[71] = t39;
    $[72] = t40;
  } else {
    t40 = $[72];
  }
  let t41;
  let t42;
  if ($[73] !== stakingContract) {
    t41 = {
      ...stakingContract,
      functionName: "getRewardsPerUnitTime"
    };
    t42 = {
      ...stakingContract,
      functionName: "getRewardTokenBalance"
    };
    $[73] = stakingContract;
    $[74] = t41;
    $[75] = t42;
  } else {
    t41 = $[74];
    t42 = $[75];
  }
  const t43 = account as any;
  const t44 = stakingContract.address as any;
  let t45;
  if ($[76] !== t43 || $[77] !== t44) {
    t45 = [t43, t44];
    $[76] = t43;
    $[77] = t44;
    $[78] = t45;
  } else {
    t45 = $[78];
  }
  let t46;
  if ($[79] !== t45 || $[80] !== tokenContract) {
    t46 = {
      ...tokenContract,
      functionName: "allowance",
      args: t45
    };
    $[79] = t45;
    $[80] = tokenContract;
    $[81] = t46;
  } else {
    t46 = $[81];
  }
  let t47;
  if ($[82] !== stakingContract.address) {
    t47 = [stakingContract.address];
    $[82] = stakingContract.address;
    $[83] = t47;
  } else {
    t47 = $[83];
  }
  let t48;
  if ($[84] !== nftContract || $[85] !== t47) {
    t48 = {
      ...nftContract,
      functionName: "balanceOf",
      args: t47
    };
    $[84] = nftContract;
    $[85] = t47;
    $[86] = t48;
  } else {
    t48 = $[86];
  }
  let t49;
  if ($[87] !== t37 || $[88] !== t40 || $[89] !== t41 || $[90] !== t42 || $[91] !== t46 || $[92] !== t48) {
    t49 = [t37, t40, t41, t42, t46, t48];
    $[87] = t37;
    $[88] = t40;
    $[89] = t41;
    $[90] = t42;
    $[91] = t46;
    $[92] = t48;
    $[93] = t49;
  } else {
    t49 = $[93];
  }
  const t50 = (chainSettings[chainId as keyof typeof chainSettings] as any)?.multicall ?? fallbackMulticall;
  let t51;
  if ($[94] !== isThirdweb) {
    t51 = {
      enabled: isThirdweb
    };
    $[94] = isThirdweb;
    $[95] = t51;
  } else {
    t51 = $[95];
  }
  let t52;
  if ($[96] !== t49 || $[97] !== t50 || $[98] !== t51) {
    t52 = {
      allowFailure: false,
      contracts: t49,
      multicallAddress: t50,
      query: t51
    };
    $[96] = t49;
    $[97] = t50;
    $[98] = t51;
    $[99] = t52;
  } else {
    t52 = $[99];
  }
  const {
    data: twStakeData,
    refetch: refetchTw
  } = useReadContracts(t52);
  let t53;
  if ($[100] !== nftContract) {
    t53 = [{
      ...nftContract,
      functionName: "cost"
    }, {
      ...nftContract,
      functionName: "totalSupply"
    }, {
      ...nftContract,
      functionName: "maxSupply"
    }];
    $[100] = nftContract;
    $[101] = t53;
  } else {
    t53 = $[101];
  }
  const t54 = (chainSettings[chainId as keyof typeof chainSettings] as any)?.multicall ?? fallbackMulticall;
  let t55;
  if ($[102] !== t53 || $[103] !== t54) {
    t55 = {
      allowFailure: false,
      contracts: t53,
      multicallAddress: t54
    };
    $[102] = t53;
    $[103] = t54;
    $[104] = t55;
  } else {
    t55 = $[104];
  }
  const {
    data: nftSupply,
    refetch: refetchSupply
  } = useReadContracts(t55);
  let t56;
  if ($[105] !== dn404Contract) {
    t56 = {
      ...dn404Contract,
      functionName: "totalSupply"
    };
    $[105] = dn404Contract;
    $[106] = t56;
  } else {
    t56 = $[106];
  }
  const t57 = account as any;
  let t58;
  if ($[107] !== t57) {
    t58 = [t57];
    $[107] = t57;
    $[108] = t58;
  } else {
    t58 = $[108];
  }
  let t59;
  if ($[109] !== dn404RewardContract || $[110] !== t58) {
    t59 = {
      ...dn404RewardContract,
      functionName: "balanceOf",
      args: t58
    };
    $[109] = dn404RewardContract;
    $[110] = t58;
    $[111] = t59;
  } else {
    t59 = $[111];
  }
  const t60 = stakingContract.address as any;
  let t61;
  if ($[112] !== t60) {
    t61 = [t60];
    $[112] = t60;
    $[113] = t61;
  } else {
    t61 = $[113];
  }
  let t62;
  if ($[114] !== dn404RewardContract || $[115] !== t61) {
    t62 = {
      ...dn404RewardContract,
      functionName: "balanceOf",
      args: t61
    };
    $[114] = dn404RewardContract;
    $[115] = t61;
    $[116] = t62;
  } else {
    t62 = $[116];
  }
  const t63 = account as any;
  let t64;
  if ($[117] !== t63) {
    t64 = [t63];
    $[117] = t63;
    $[118] = t64;
  } else {
    t64 = $[118];
  }
  let t65;
  if ($[119] !== dn404Contract || $[120] !== t64) {
    t65 = {
      ...dn404Contract,
      functionName: "balanceOf",
      args: t64
    };
    $[119] = dn404Contract;
    $[120] = t64;
    $[121] = t65;
  } else {
    t65 = $[121];
  }
  const t66 = account as any;
  const t67 = dn404RewardContract.address as any;
  let t68;
  if ($[122] !== t66 || $[123] !== t67) {
    t68 = [t66, t67];
    $[122] = t66;
    $[123] = t67;
    $[124] = t68;
  } else {
    t68 = $[124];
  }
  let t69;
  if ($[125] !== t68 || $[126] !== tokenContract) {
    t69 = {
      ...tokenContract,
      functionName: "allowance",
      args: t68
    };
    $[125] = t68;
    $[126] = tokenContract;
    $[127] = t69;
  } else {
    t69 = $[127];
  }
  let t70;
  if ($[128] !== t56 || $[129] !== t59 || $[130] !== t62 || $[131] !== t65 || $[132] !== t69) {
    t70 = [t56, t59, t62, t65, t69];
    $[128] = t56;
    $[129] = t59;
    $[130] = t62;
    $[131] = t65;
    $[132] = t69;
    $[133] = t70;
  } else {
    t70 = $[133];
  }
  const t71 = (chainSettings[chainId as keyof typeof chainSettings] as any)?.multicall ?? fallbackMulticall;
  let t72;
  if ($[134] !== t70 || $[135] !== t71) {
    t72 = {
      allowFailure: false,
      contracts: t70,
      multicallAddress: t71
    };
    $[134] = t70;
    $[135] = t71;
    $[136] = t72;
  } else {
    t72 = $[136];
  }
  const {
    data: dn404Data,
    refetch: refetch404
  } = useReadContracts(t72);
  let t73;
  if ($[137] !== stakingContract.address) {
    t73 = [stakingContract.address];
    $[137] = stakingContract.address;
    $[138] = t73;
  } else {
    t73 = $[138];
  }
  let t74;
  if ($[139] !== nftContract || $[140] !== t73) {
    t74 = {
      ...nftContract,
      functionName: "balanceOf",
      args: t73
    };
    $[139] = nftContract;
    $[140] = t73;
    $[141] = t74;
  } else {
    t74 = $[141];
  }
  let t75;
  if ($[142] !== stakingContract) {
    t75 = {
      ...stakingContract,
      functionName: "rewardsAmount"
    };
    $[142] = stakingContract;
    $[143] = t75;
  } else {
    t75 = $[143];
  }
  let t76;
  if ($[144] !== t74 || $[145] !== t75) {
    t76 = [t74, t75];
    $[144] = t74;
    $[145] = t75;
    $[146] = t76;
  } else {
    t76 = $[146];
  }
  const t77 = (chainSettings[chainId as keyof typeof chainSettings] as any)?.multicall ?? fallbackMulticall;
  let t78;
  if ($[147] !== t76 || $[148] !== t77) {
    t78 = {
      allowFailure: false,
      contracts: t76,
      multicallAddress: t77
    };
    $[147] = t76;
    $[148] = t77;
    $[149] = t78;
  } else {
    t78 = $[149];
  }
  const {
    data: nftData,
    refetch: refetchNft
  } = useReadContracts(t78);
  let t79;
  if ($[150] !== refetch || $[151] !== refetch404 || $[152] !== refetchNft || $[153] !== refetchSupply || $[154] !== refetchTw || $[155] !== refreshData || $[156] !== refreshIds) {
    t79 = () => {
      refetch();
      refetchNft();
      refreshData();
      refreshIds();
      refetch404();
      refetchTw();
      refetchSupply();
    };
    $[150] = refetch;
    $[151] = refetch404;
    $[152] = refetchNft;
    $[153] = refetchSupply;
    $[154] = refetchTw;
    $[155] = refreshData;
    $[156] = refreshIds;
    $[157] = t79;
  } else {
    t79 = $[157];
  }
  const refresh = t79;
  let t80;
  if ($[158] !== account || $[159] !== currPool._404TokenDecimals || $[160] !== currPool.is404 || $[161] !== currPool.nftMaxSupply || $[162] !== currPool.rewardDecimals || $[163] !== dn404Data || $[164] !== isThirdweb || $[165] !== nftData || $[166] !== nftSupply || $[167] !== stakedTokenIds || $[168] !== tokenData || $[169] !== tokenIds) {
    t80 = () => {
      if (account && tokenData) {
        setIsApproved((tokenData as any)?.[0]);
        setNftBalance(Number((tokenData as any)?.[1]));
        setTokenBalance(Number(formatUnits((tokenData as any)?.[2], currPool.rewardDecimals)));
        setRewardBalanceBn((tokenData as any)?.[2]);
        if (!isThirdweb) {
          setStakingRewardBalance(Number(formatUnits((tokenData as any)?.[3], currPool.rewardDecimals)));
        }
      }
      if (nftSupply) {
        setMaxSupply(currPool.nftMaxSupply);
        setNftCost(Number(formatUnits((nftSupply as any)?.[0], 18)));
        setTotalSupply(Number((nftSupply as any)?.[1]));
      }
      if (nftData) {
        setTotalStakedNft(Number((nftData as any)?.[0]));
        setMaxSupply(currPool.nftMaxSupply);
      }
      if (dn404Data) {
        setDn404TotalSupply(Number(formatUnits((dn404Data as any)?.[0], currPool._404TokenDecimals)));
        setDn404RewardBalance(Number(formatUnits((dn404Data as any)?.[1], currPool.rewardDecimals)));
        setDn404RewardBalanceBn((dn404Data as any)?.[1]);
        setStaking404RewardBalance(Number(formatUnits((dn404Data as any)?.[2], currPool.rewardDecimals)));
        setDn404TokenBalance(Number(formatUnits((dn404Data as any)?.[3], currPool._404TokenDecimals)));
        if (currPool.is404) {
          setWrapAllowance(Number(formatUnits((dn404Data as any)?.[4], currPool.rewardDecimals)));
        }
      }
      if (account && tokenIds) {
        setMyTokenIds(tokenIds);
      }
      if (account && stakedTokenIds) {
        setMyStakedTokenIds(stakedTokenIds);
      }
      if (!account) {
        setMyTokenIds([]);
        setMyStakedTokenIds([]);
      }
    };
    $[158] = account;
    $[159] = currPool._404TokenDecimals;
    $[160] = currPool.is404;
    $[161] = currPool.nftMaxSupply;
    $[162] = currPool.rewardDecimals;
    $[163] = dn404Data;
    $[164] = isThirdweb;
    $[165] = nftData;
    $[166] = nftSupply;
    $[167] = stakedTokenIds;
    $[168] = tokenData;
    $[169] = tokenIds;
    $[170] = t80;
  } else {
    t80 = $[170];
  }
  let t81;
  if ($[171] !== account || $[172] !== currPool || $[173] !== dn404Data || $[174] !== isThirdweb || $[175] !== nftData || $[176] !== nftSupply || $[177] !== poolId || $[178] !== stakedTokenIds || $[179] !== tokenData || $[180] !== tokenIds) {
    t81 = [tokenData, account, poolId, tokenIds, stakedTokenIds, nftData, dn404Data, nftSupply, currPool, isThirdweb];
    $[171] = account;
    $[172] = currPool;
    $[173] = dn404Data;
    $[174] = isThirdweb;
    $[175] = nftData;
    $[176] = nftSupply;
    $[177] = poolId;
    $[178] = stakedTokenIds;
    $[179] = tokenData;
    $[180] = tokenIds;
    $[181] = t81;
  } else {
    t81 = $[181];
  }
  useEffect(t80, t81);
  let t82;
  if ($[182] !== account || $[183] !== currPool.rewardDecimals || $[184] !== isThirdweb || $[185] !== stakeData) {
    t82 = () => {
      if (!isThirdweb && stakeData) {
        setTotalStaked(Number((stakeData as any)?.[0]));
        setIsOwner(String((stakeData as any)?.[1]) === account);
        setRewardsAmount(Number(formatUnits((stakeData as any)?.[2], currPool.rewardDecimals)));
      }
    };
    $[182] = account;
    $[183] = currPool.rewardDecimals;
    $[184] = isThirdweb;
    $[185] = stakeData;
    $[186] = t82;
  } else {
    t82 = $[186];
  }
  let t83;
  if ($[187] !== account || $[188] !== currPool.rewardDecimals || $[189] !== isThirdweb || $[190] !== poolId || $[191] !== stakeData) {
    t83 = [account, poolId, stakeData, isThirdweb, currPool.rewardDecimals];
    $[187] = account;
    $[188] = currPool.rewardDecimals;
    $[189] = isThirdweb;
    $[190] = poolId;
    $[191] = stakeData;
    $[192] = t83;
  } else {
    t83 = $[192];
  }
  useEffect(t82, t83);
  let t84;
  if ($[193] !== currPool.rewardDecimals || $[194] !== isThirdweb || $[195] !== twStakeData) {
    t84 = () => {
      if (isThirdweb && twStakeData) {
        const twTotalStaked = twStakeData[0][0].length;
        const twStakedIds = twStakeData[0][0];
        setTotalStaked(twTotalStaked);
        setIsOwner(twStakeData[1]);
        setRewardsAmount(Number(formatUnits(twStakeData[2], currPool.rewardDecimals)));
        setTwPendingRewards(Number(formatUnits(twStakeData[0][1], currPool.rewardDecimals)));
        setStakingRewardBalance(Number(formatUnits(twStakeData[3], currPool.rewardDecimals)));
        setTwAllowance(Number(formatUnits(twStakeData[4], currPool.rewardDecimals)));
        setTotalStakedNft(Number((twStakeData as any)?.[5]));
        if (twTotalStaked > 0) {
          const stakedIdsArray = [];
          for (let i = 0; i < twTotalStaked; i++) {
            stakedIdsArray[i] = Number(twStakedIds[i]);
          }
          setTwTokenIds(stakedIdsArray);
        }
      }
    };
    $[193] = currPool.rewardDecimals;
    $[194] = isThirdweb;
    $[195] = twStakeData;
    $[196] = t84;
  } else {
    t84 = $[196];
  }
  let t85;
  if ($[197] !== account || $[198] !== currPool.rewardDecimals || $[199] !== isThirdweb || $[200] !== twStakeData) {
    t85 = [account, currPool.rewardDecimals, isThirdweb, twStakeData];
    $[197] = account;
    $[198] = currPool.rewardDecimals;
    $[199] = isThirdweb;
    $[200] = twStakeData;
    $[201] = t85;
  } else {
    t85 = $[201];
  }
  useEffect(t84, t85);
  const t86 = isThirdweb ? twTokenIds : stakedTokenIds;
  const t87 = isThirdweb ? twPendingRewards : pendingRewards;
  const t88 = isThirdweb ? twTokenIds : stakedTokenIds;
  const t89 = isThirdweb ? twPendingRewards : pendingRewards;
  let t90;
  if ($[202] !== dn404RewardBalance || $[203] !== dn404RewardBalanceBn || $[204] !== dn404TokenBalance || $[205] !== dn404TotalSupply || $[206] !== isApproved || $[207] !== isOwner || $[208] !== maxSupply || $[209] !== myTokenIds || $[210] !== nftBalance || $[211] !== nftCost || $[212] !== refresh || $[213] !== rewardBalanceBn || $[214] !== rewardsAmount || $[215] !== staking404RewardBalance || $[216] !== stakingRewardBalance || $[217] !== t88 || $[218] !== t89 || $[219] !== tokenBalance || $[220] !== totalStaked || $[221] !== totalStakedNft || $[222] !== totalSupply || $[223] !== twAllowance || $[224] !== wrapAllowance) {
    t90 = {
      wrapAllowance,
      rewardsAmount,
      isApproved,
      tokenBalance,
      totalStaked,
      nftBalance,
      myTokenIds,
      stakedTokenIds: t88,
      pendingRewards: t89,
      refresh,
      nftCost,
      twAllowance,
      totalSupply,
      maxSupply,
      totalStakedNft,
      dn404TotalSupply,
      rewardBalanceBn,
      dn404RewardBalance,
      dn404RewardBalanceBn,
      isOwner,
      stakingRewardBalance,
      staking404RewardBalance,
      dn404TokenBalance
    };
    $[202] = dn404RewardBalance;
    $[203] = dn404RewardBalanceBn;
    $[204] = dn404TokenBalance;
    $[205] = dn404TotalSupply;
    $[206] = isApproved;
    $[207] = isOwner;
    $[208] = maxSupply;
    $[209] = myTokenIds;
    $[210] = nftBalance;
    $[211] = nftCost;
    $[212] = refresh;
    $[213] = rewardBalanceBn;
    $[214] = rewardsAmount;
    $[215] = staking404RewardBalance;
    $[216] = stakingRewardBalance;
    $[217] = t88;
    $[218] = t89;
    $[219] = tokenBalance;
    $[220] = totalStaked;
    $[221] = totalStakedNft;
    $[222] = totalSupply;
    $[223] = twAllowance;
    $[224] = wrapAllowance;
    $[225] = t90;
  } else {
    t90 = $[225];
  }
  let t91;
  if ($[226] !== dn404RewardBalance || $[227] !== dn404RewardBalanceBn || $[228] !== dn404TokenBalance || $[229] !== dn404TotalSupply || $[230] !== isApproved || $[231] !== isOwner || $[232] !== maxSupply || $[233] !== myTokenIds || $[234] !== nftBalance || $[235] !== nftCost || $[236] !== refresh || $[237] !== rewardBalanceBn || $[238] !== rewardsAmount || $[239] !== staking404RewardBalance || $[240] !== stakingRewardBalance || $[241] !== t86 || $[242] !== t87 || $[243] !== t90 || $[244] !== tokenBalance || $[245] !== totalStaked || $[246] !== totalStakedNft || $[247] !== totalSupply || $[248] !== twAllowance || $[249] !== wrapAllowance) {
    t91 = {
      wrapAllowance,
      rewardsAmount,
      isApproved,
      tokenBalance,
      totalStaked,
      nftBalance,
      myTokenIds,
      stakedTokenIds: t86,
      pendingRewards: t87,
      refresh,
      nftCost,
      totalSupply,
      twAllowance,
      maxSupply,
      totalStakedNft,
      dn404TotalSupply,
      rewardBalanceBn,
      dn404RewardBalance,
      dn404RewardBalanceBn,
      isOwner,
      stakingRewardBalance,
      staking404RewardBalance,
      dn404TokenBalance,
      allPoolData: t90
    };
    $[226] = dn404RewardBalance;
    $[227] = dn404RewardBalanceBn;
    $[228] = dn404TokenBalance;
    $[229] = dn404TotalSupply;
    $[230] = isApproved;
    $[231] = isOwner;
    $[232] = maxSupply;
    $[233] = myTokenIds;
    $[234] = nftBalance;
    $[235] = nftCost;
    $[236] = refresh;
    $[237] = rewardBalanceBn;
    $[238] = rewardsAmount;
    $[239] = staking404RewardBalance;
    $[240] = stakingRewardBalance;
    $[241] = t86;
    $[242] = t87;
    $[243] = t90;
    $[244] = tokenBalance;
    $[245] = totalStaked;
    $[246] = totalStakedNft;
    $[247] = totalSupply;
    $[248] = twAllowance;
    $[249] = wrapAllowance;
    $[250] = t91;
  } else {
    t91 = $[250];
  }
  return t91;
}